import BannerNotification from '@atoms/BannerNotification/BannerNotification';
import Button from '@atoms/Button/Button';
import Drawer, { IDrawerContentRef } from '@atoms/Drawer/Drawer';
import LoadingOverlay from '@atoms/LoadingOverlay/LoadingOverlay';
import { IAddToCartTray, IBasketDetails } from '@models/IAddToCartTray';
import { ICartItem } from '@models/ICartItem';
import { ICrossSellTabbedProductsList } from '@models/ICrossSellTabbedProductsList';
import { IconName } from '@models/IIcon';
import { IProductTrackingData } from '@models/IProductTrackingData';
import AddToCartErrorEnum from '@models/enums/AddToCartErrorEnum';
import BannerNotificationStylesEnum from '@models/enums/BannerNotificationStylesEnum';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import TagTypesEnum from '@models/enums/TagTypesEnum';
import CartItem from '@molecules/CartItem/CartItem';
import PromoBanner from '@molecules/PromoBanner/PromoBanner';
import PromoCartItem from '@molecules/PromoCartItem/PromoCartItem';
import TabbedCrossSellProductListing from '@molecules/TabbedCrossSellProductListing/TabbedCrossSellProductListing';
import useCartSizeStore from '@state/CartSizeStore';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import axios, { AxiosError } from 'axios';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Cookies from 'js-cookie';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

interface Props extends IAddToCartTray {
  readonly addToCartErrorLabel: string;
  readonly isAddingToCartFromStickyBuyBar?: boolean;
  readonly isOpen: boolean;
  readonly mainItemTrackingData?: IProductTrackingData;
  readonly onSetIsOpen: Dispatch<SetStateAction<boolean>>;
}

const AddToCartTray = ({
  addToCartErrorLabel,
  addedItem,
  amountSaveLabel,
  apiDownLabel,
  basketLink,
  basketProducts,
  basketSlideOutTrayTitleLabel,
  cartItems,
  cartRemoveItemLabel,
  changeCartItemEndpoint,
  checkoutLink,
  closeLabel,
  emptyBasketLabel,
  giftItemsNotification,
  hasMaxBasketQuantityWarning,
  includingTaxLabel,
  isAddingToCartFromStickyBuyBar,
  isOpen,
  itemsCount,
  itemsLabel,
  mainItemTrackingData,
  maxBasketQuantityErrorNotification,
  maxBasketQuantityWarningNotification,
  maxBasketValueErrorNotification,
  promoBanner,
  removeItemFromCartEndpoint,
  showLessLabel,
  showMoreLabel,
  subtotalFormattedBasketValue,
  tabbedProductsList,
  totalBasketValue,
  totalFormattedBasketValue,
  totalLabel,
  onSetIsOpen,
}: Props) => {
  const isMobile = useMediaQuery(768);
  const isMobileScreen = useMediaQuery(768, BreakpointTypeEnum.Screen);
  const isZoomedDesktop = !isMobileScreen && isMobile;
  const drawerContentRef = useRef<IDrawerContentRef>(null);
  const [totalPriceBannerRef, { scrollHeight: totalPriceBannerHeight }] =
    useResizeObserver();
  const [hasApiError, setHasApiError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentAddedItem, setCurrentAddedItem] = useState<ICartItem>();
  const [isPromoProductsSectionDisplayed, setIsPromoProductsSectionDisplayed] =
    useState(false);

  const [basketDetails, setBasketDetails] = useState<IBasketDetails>({
    basketProducts,
    totalBasketValue,
    cartItems,
    promoBanner,
    subtotalFormattedBasketValue,
    totalFormattedBasketValue,
    itemsCount,
    itemsLabel,
  });

  const [
    currentHasMaxBasketQuantityWarning,
    setCurrentHasMaxBasketQuantityWarning,
  ] = useState(!!hasMaxBasketQuantityWarning);
  const [hasMaxBasketQuantityError, setHasMaxBasketQuantityError] =
    useState(false);
  const [hasMaxBasketValueError, setHasMaxBasketValueError] = useState(false);
  const [maxAllowedProductQuantityError, setMaxAllowedProductQuantityError] =
    useState(false);
  const [crossSellTabbedItems, setCrossSellTabbedItems] =
    useState(tabbedProductsList);

  const shouldDisplayCrossSellItems =
    !!crossSellTabbedItems.tabs?.length &&
    (!basketDetails.promoBanner || !isPromoProductsSectionDisplayed);

  const hasMaxBasketError = hasMaxBasketQuantityError || hasMaxBasketValueError;

  const displayedBannerNotification = (() => {
    if (currentHasMaxBasketQuantityWarning)
      return maxBasketQuantityWarningNotification;
    if (hasMaxBasketQuantityError) return maxBasketQuantityErrorNotification;
    if (hasMaxBasketValueError) return maxBasketValueErrorNotification;
    if (maxAllowedProductQuantityError)
      return {
        text: addToCartErrorLabel,
        displayStyle: BannerNotificationStylesEnum.Error,
        icon: 'warning' as IconName,
      };

    return null;
  })();

  const { cartSizeName, setCartSize } = useCartSizeStore((state) => ({
    cartSizeName: state.cartSizeName,
    setCartSize: state.setCartSize,
  }));

  // This is for when we close the modal and open it back without refreshing the page
  // In this case, the component does not remount and we need to update a couple of things
  useEffect(() => {
    if (isOpen) {
      setCurrentAddedItem(addedItem);
      setBasketDetails({
        basketProducts,
        totalBasketValue,
        cartItems,
        subtotalFormattedBasketValue,
        totalFormattedBasketValue,
        itemsCount,
        itemsLabel,
        promoBanner,
      });
      setCurrentHasMaxBasketQuantityWarning(!!hasMaxBasketQuantityWarning);
      setMaxAllowedProductQuantityError(false);
      setCrossSellTabbedItems(tabbedProductsList);
    }
  }, [
    addedItem,
    basketProducts,
    cartItems,
    hasMaxBasketQuantityWarning,
    isOpen,
    itemsCount,
    itemsLabel,
    promoBanner,
    subtotalFormattedBasketValue,
    tabbedProductsList,
    totalBasketValue,
    totalFormattedBasketValue,
  ]);

  const addToCartErrorHandler = (error: AxiosError) => {
    const errorCode = error.response?.data?.errorCode;
    const scrollToTop = () => drawerContentRef.current?.scrollToTop();

    switch (errorCode) {
      case AddToCartErrorEnum.maxBasketQuantity:
        setHasMaxBasketQuantityError(true);
        break;
      case AddToCartErrorEnum.maxBasketValue:
        setHasMaxBasketValueError(true);
        break;
      default:
        setMaxAllowedProductQuantityError(true);
        break;
    }

    scrollToTop();
  };

  const handleCrossSellItemRemoval = (code: string) => {
    const isCrossSellItem = !!crossSellTabbedItems.tabs
      ?.flatMap((tab) => tab.items)
      .find((item) => item.sku === code);
    if (!isCrossSellItem) return;

    setCrossSellTabbedItems((prevCrossSellTabbedItems) => ({
      ...prevCrossSellTabbedItems,
      tabs: prevCrossSellTabbedItems.tabs?.map((tab) => ({
        ...tab,
        items: tab.items.map((item) =>
          item.sku === code ? { ...item, inCart: false } : item
        ),
      })),
    }));
  };

  const handleRemoveItemFromCart =
    (onSuccessfulRemoveItemFromCart?: () => void) =>
    async (code: string, isGift?: boolean) => {
      try {
        setIsLoading(true);

        const { data } = await axios.post<IBasketDetails>(
          removeItemFromCartEndpoint,
          {
            code,
            isGift,
          }
        );

        setBasketDetails(data);
        setIsLoading(false);
        setCartSize(Number(Cookies.get(cartSizeName)) || 0);
        setCurrentHasMaxBasketQuantityWarning(false);
        setHasMaxBasketQuantityError(false);
        setHasMaxBasketValueError(false);
        setMaxAllowedProductQuantityError(false);
        onSuccessfulRemoveItemFromCart?.();
        handleCrossSellItemRemoval(code);

        const currentItemTrackingData = basketDetails.basketProducts?.find(
          (product) => product.item_id === code
        );
        const currentItemQuantity = basketDetails.cartItems.find(
          (item) => item.code === code
        )?.selectedQuantity;

        if (currentItemTrackingData && currentItemQuantity) {
          updateDataLayer({
            event: 'removeFromCart',
            ecommerce: {
              currency: mainItemTrackingData?.currency,
              items: [
                {
                  ...currentItemTrackingData,
                  quantity: 0,
                },
              ],
              value: data.totalBasketValue,
              quantityChange: -currentItemQuantity,
            },
          });
        }
      } catch {
        setHasApiError(true);
        setIsLoading(false);
      }
    };

  const handleChangeCartItemQuantity =
    (onSuccessfulChangeCartItemQuantity?: () => void) =>
    async (code: string, quantity: number) => {
      try {
        setIsLoading(true);

        const { data } = await axios.post<IAddToCartTray>(
          changeCartItemEndpoint,
          { code, quantity, mainItemCode: currentAddedItem?.code }
        );

        setBasketDetails({
          basketProducts: data.basketProducts,
          totalBasketValue: data.totalBasketValue,
          cartItems: data.cartItems,
          subtotalFormattedBasketValue: data.subtotalFormattedBasketValue,
          totalFormattedBasketValue: data.totalFormattedBasketValue,
          itemsCount: data.itemsCount,
          itemsLabel: data.itemsLabel,
          promoBanner: data.promoBanner,
        });

        setCrossSellTabbedItems(data.tabbedProductsList);
        setIsLoading(false);
        setCartSize(Number(Cookies.get(cartSizeName)) || 0);
        setHasMaxBasketQuantityError(false);
        setHasMaxBasketValueError(false);
        setMaxAllowedProductQuantityError(false);
        setCurrentHasMaxBasketQuantityWarning(
          !!data.hasMaxBasketQuantityWarning
        );
        onSuccessfulChangeCartItemQuantity?.();

        if (data.hasMaxBasketQuantityWarning)
          drawerContentRef.current?.scrollToTop();

        const currentItemTrackingData = data.basketProducts?.find(
          (product) => product.item_id === code
        );
        if (currentItemTrackingData) {
          updateDataLayer({
            event: quantity > 0 ? 'addToCart' : 'removeFromCart',
            ...(isAddingToCartFromStickyBuyBar && quantity > 0
              ? { content_type: 'sticky_buy_bar' }
              : {}),
            ecommerce: {
              currency: mainItemTrackingData?.currency,
              items: [
                {
                  ...currentItemTrackingData,
                  quantity: data.addedItem.selectedQuantity,
                },
              ],
              value: data.totalBasketValue,
              quantityChange: quantity,
            },
          });
        }
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
        console.log({ error });
        setIsLoading(false);
        setCurrentHasMaxBasketQuantityWarning(false);
        addToCartErrorHandler(error as AxiosError);
      }
    };

  const updateCrossSellItemDataLayer = (
    currentTabbedProductsList: ICrossSellTabbedProductsList,
    currentTotalBasketValue: number,
    code: string,
    isAddToCartOperation: boolean
  ) => {
    const itemTrackingData = currentTabbedProductsList.tabs
      ?.flatMap((tab) => tab.items)
      .find((item) => item.trackingData?.id === code)?.trackingData;

    if (itemTrackingData)
      updateDataLayer({
        event: isAddToCartOperation ? 'addToCart' : 'removeFromCart',
        content_type: 'slide out tray',
        ecommerce: {
          currency: itemTrackingData.currency,
          items: [{ ...mapToNewTrackingData(itemTrackingData), quantity: 1 }],
          value: currentTotalBasketValue,
          ...(isAddToCartOperation ? {} : { removedItemId: code }),
        },
      });
  };

  const handleAddCrossSellItemToCart =
    (
      onSuccessfulAddToCart: (
        tabbedProductsList: ICrossSellTabbedProductsList
      ) => void
    ) =>
    async (code: string) => {
      try {
        setIsLoading(true);

        const { data } = await axios.post<IAddToCartTray>(
          changeCartItemEndpoint,
          {
            code,
            quantity: 1,
            mainItemCode: currentAddedItem?.code,
          }
        );

        onSuccessfulAddToCart(data.tabbedProductsList);
        setIsLoading(false);
        setCartSize(Number(Cookies.get(cartSizeName)) || 0);
        setBasketDetails({
          basketProducts: data.basketProducts,
          totalBasketValue: data.totalBasketValue,
          cartItems: data.cartItems,
          subtotalFormattedBasketValue: data.subtotalFormattedBasketValue,
          totalFormattedBasketValue: data.totalFormattedBasketValue,
          itemsCount: data.itemsCount,
          itemsLabel: data.itemsLabel,
          promoBanner: data.promoBanner,
        });

        setCurrentHasMaxBasketQuantityWarning(
          !!data.hasMaxBasketQuantityWarning
        );

        setCrossSellTabbedItems(data.tabbedProductsList);
        updateCrossSellItemDataLayer(
          data.tabbedProductsList,
          data.totalBasketValue,
          code,
          true
        );
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
        console.log({ error });
        addToCartErrorHandler(error as AxiosError);
        setIsLoading(false);
      }
    };

  const handleRemoveCrossSellItemFromCart =
    (onSuccessfulRemoveFromCart: () => void) => async (code: string) => {
      try {
        setIsLoading(true);

        const { data } = await axios.post<IBasketDetails>(
          removeItemFromCartEndpoint,
          { code }
        );

        onSuccessfulRemoveFromCart();
        setIsLoading(false);
        setCartSize(Number(Cookies.get(cartSizeName)) || 0);
        setBasketDetails(data);

        updateCrossSellItemDataLayer(
          crossSellTabbedItems,
          data.totalBasketValue,
          code,
          false
        );
      } catch {
        setHasApiError(true);
        setIsLoading(false);
      }
    };

  return (
    <Drawer
      ref={drawerContentRef}
      isOpen={isOpen && isMobile !== undefined}
      setIsOpen={onSetIsOpen}
      maxWidthClassName="max-w-[979px]"
      contentSpacingClassName={classNames(
        'md:pb-6 md:pt-[120px]',
        isZoomedDesktop ? 'pt-7.5' : 'py-7.5'
      )}
      contentClassName="overflow-y-auto"
      isBottomToTopSliding={isMobile}
      dataTestId="CT-149-Add-To-Basket-Slide-Out-Tray"
      closeButtonClassNames="rounded-full border-2 border-black-100 p-2.5 focus:outline-2 focus:outline-yellow"
      closeButtonPositionClassName="top-7.5 right-5 md:right-10 md:top-10"
      backgroundColourClassName="bg-white"
      style={{
        paddingBottom: isZoomedDesktop ? 0 : totalPriceBannerHeight + 20,
      }}
    >
      <LoadingOverlay isLoading={isLoading} />

      {(basketDetails.cartItems || []).length === 0 ? (
        <motion.div
          className="px-5 max-md:mt-14 md:px-10"
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
        >
          <h2 className="h4" data-testid="Empty-Basket-Label">
            {emptyBasketLabel}
          </h2>
        </motion.div>
      ) : (
        <>
          <div className="mb-5 flex items-baseline gap-x-6 px-5 md:absolute md:top-10 md:left-10 md:mb-0 md:p-0">
            <h2 className="h4">{basketSlideOutTrayTitleLabel}</h2>

            <p className="h6 text-grey-450">
              {basketDetails.itemsCount} {basketDetails.itemsLabel}
            </p>
          </div>

          <motion.div
            key="cartDetails"
            className="px-5 md:px-10"
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          >
            <div
              className={classNames(
                'mb-10 hidden flex-wrap items-center gap-5 md:flex',
                hasMaxBasketError ? 'justify-end' : 'justify-between'
              )}
            >
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5">
                <Button
                  link={checkoutLink}
                  dataTestId="CT-149-Add-To-Basket-Slide-Out-Tray-Checkout-Link"
                  linkCallback={() => {
                    updateDataLayer({
                      event: 'beginCheckout',
                      ecommerce: {
                        currency: mainItemTrackingData?.currency,
                        items: basketDetails.basketProducts,
                        value: basketDetails.totalBasketValue,
                      },
                    });
                  }}
                />

                <Button
                  link={basketLink}
                  isLightMode
                  secondary
                  dataTestId="CT-149-Add-To-Basket-Slide-Out-Tray-Basket-Link"
                />
              </div>
            </div>

            {displayedBannerNotification && (
              <BannerNotification
                displayStyle={displayedBannerNotification.displayStyle}
                iconName={displayedBannerNotification.icon}
                richText={displayedBannerNotification.text}
                link={displayedBannerNotification.link}
                className={classNames('mb-7.5 md:mb-10', {
                  'max-md:mt-7.5': hasMaxBasketError,
                })}
              />
            )}

            <div className="flex flex-col gap-y-5">
              <AnimatePresence>
                <>
                  {basketDetails.cartItems?.map((cartItem, index) => (
                    <CartItem
                      key={`${cartItem.code}-${!!cartItem.isFreeItem}`}
                      {...cartItem}
                      amountSaveLabel={amountSaveLabel}
                      showMoreLabel={showMoreLabel}
                      showLessLabel={showLessLabel}
                      desktopBreakpoint={BreakpointEnum.Medium}
                      onChangeCartItemQuantity={handleChangeCartItemQuantity()}
                      onRemoveItemFromCart={handleRemoveItemFromCart()}
                      index={index}
                      giftNotification={
                        cartItem.isFreeItem ? giftItemsNotification : undefined
                      }
                      isInTray
                    />
                  ))}

                  {basketDetails?.promoBanner?.isDiscountApplied && (
                    <motion.div
                      key="promoItem"
                      layout="position"
                      transition={{
                        type: 'spring',
                        duration: 0.3,
                        layout: { type: 'spring', duration: 0.7 },
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                    >
                      <PromoCartItem
                        {...basketDetails.promoBanner.promoCartItem}
                      />
                    </motion.div>
                  )}
                </>
              </AnimatePresence>
            </div>
          </motion.div>

          <AnimatePresence>
            {basketDetails?.promoBanner && (
              <motion.div
                layout="position"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="mt-5 px-5 md:mt-10 md:px-10"
              >
                <PromoBanner
                  {...basketDetails.promoBanner}
                  isInTray
                  onAddItemToCart={handleChangeCartItemQuantity}
                  onRemoveItemFromCart={handleRemoveItemFromCart}
                  onProductsSectionDisplayed={
                    setIsPromoProductsSectionDisplayed
                  }
                />

                {shouldDisplayCrossSellItems && (
                  <div className="mt-7.5 h-px bg-grey md:mt-10" />
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {shouldDisplayCrossSellItems && (
              <motion.div
                layout="position"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={promoBanner ? 'mt-7.5 md:mt-10' : 'mt-15 md:mt-24'}
              >
                <TabbedCrossSellProductListing
                  itemsPayload={crossSellTabbedItems}
                  isLightMode
                  cardClassName="border border-grey"
                  headerHorizontalSpacingClassName="px-5 md:px-10"
                  carouselHorizontalSpacingClassName="px-5 md:px-10"
                  titleClassName="h6 px-5 md:px-10"
                  trackingContentType="slide out tray"
                  defaultTabType={TagTypesEnum.Five}
                  onAddItemToCart={handleAddCrossSellItemToCart}
                  onRemoveItemFromCart={handleRemoveCrossSellItemFromCart}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            <motion.div
              className={classNames(
                'bottom-0 z-max w-full bg-grey-600 p-5 text-white lg:px-10 lg:py-8',
                isZoomedDesktop ? 'mt-5' : 'fixed'
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              ref={totalPriceBannerRef}
            >
              <div
                data-testid="CT-149-Add-To-Basket-Slide-Out-Tray-Sticky-Bottom-Bar"
                className="flex max-md:mb-5 max-md:flex-col md:justify-between"
              >
                <p className="body-one">
                  {totalLabel}{' '}
                  <span className="text-grey-500">{includingTaxLabel}</span>
                </p>
                <p>
                  <span className="body-one body-one--bold">
                    {basketDetails.totalFormattedBasketValue}
                  </span>

                  {basketDetails.totalFormattedBasketValue !==
                    basketDetails.subtotalFormattedBasketValue && (
                    <span className="body-one ml-2.5 text-grey-400 line-through decoration-1">
                      {basketDetails.subtotalFormattedBasketValue}
                    </span>
                  )}
                </p>
              </div>

              <div className="flex flex-wrap gap-5 md:hidden">
                <Button
                  link={checkoutLink}
                  dataTestId="CT-149-Add-To-Basket-Slide-Out-Tray-Checkout-Link"
                  linkCallback={() => {
                    updateDataLayer({
                      event: 'beginCheckout',
                      ecommerce: {
                        currency: mainItemTrackingData?.currency,
                        items: basketDetails.basketProducts,
                        value: basketDetails.totalBasketValue,
                      },
                    });
                  }}
                />

                <Button
                  className="text-center text-white dark:text-black"
                  link={basketLink}
                  isLightMode
                  secondary
                  dataTestId="CT-149-Add-To-Basket-Slide-Out-Tray-Basket-Link"
                />
              </div>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence>
            {hasApiError && (
              <motion.div
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <BannerNotification
                  closeLabel={closeLabel}
                  caption={apiDownLabel}
                  displayStyle={BannerNotificationStylesEnum.Error}
                  iconName="warning"
                  className="fixed right-0 bottom-5 z-[9999] mx-5 max-w-[650px] bg-white md:right-2.5"
                  title={cartRemoveItemLabel}
                  onDismissNotification={() => setHasApiError(false)}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </Drawer>
  );
};

export default AddToCartTray;
